// Galeria mediów produktu (@theme/media-gallery) — 12-pdp-galeria; kontrakt z main-product: ARCHITEKTURA §17.2.
// Publiczne: <media-gallery>.select(index, {animate}), .showMedia(mediaId, {animate}); eksport dla zoom.js.
import { Theme, EVENTS, loadStyle, onIdle, reducedMotion } from '@theme/core';

const MODEL_CSS = 'https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css';
export const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

// Koniec przewijania: `scrollend`, a bez niego 120 ms ciszy. Zwraca funkcję, która kończy od razu.
export function afterScroll(el, done) {
  let timer;
  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    clearTimeout(timer);
    el.removeEventListener('scroll', wait);
    el.removeEventListener('scrollend', finish);
    done();
  };
  const wait = () => {
    clearTimeout(timer);
    timer = setTimeout(finish, 120);
  };
  el.addEventListener('scroll', wait, { passive: true });
  el.addEventListener('scrollend', finish);
  wait();
  return finish;
}

// Maska zanikania krawędzi przewijanej listy miniatur (12 §4.3): wg procentu przewinięcia, bez maski, gdy lista się mieści.
export function scrollMask(el) {
  const vertical = el.scrollHeight > el.clientHeight + 1;
  const range = vertical ? el.scrollHeight - el.clientHeight : el.scrollWidth - el.clientWidth;
  if (range <= 1) return void (el.style.maskImage = '');
  const p = Math.abs(vertical ? el.scrollTop : el.scrollLeft) / range;
  el.style.maskImage = `linear-gradient(to ${vertical ? 'bottom' : 'right'}, transparent ${p > 0 ? 1 : 0}%, #000 ${Math.min(p, 0.1) * 100}%, #000 ${Math.max(p, 0.9) * 100}%, transparent 100%)`;
}

let zoomPreloaded = false;
function preloadZoom() {
  if (zoomPreloaded || !Theme.assets?.zoom) return;
  zoomPreloaded = true;
  document.head.append(Object.assign(document.createElement('link'), { rel: 'modulepreload', href: Theme.assets.zoom }));
}

function afterLoad(callback) {
  if (document.readyState === 'complete') onIdle(callback);
  else addEventListener('load', () => onIdle(callback), { once: true });
}

// ----- Wideo i modele 3D (FR-12-13/14): jedno odtwarzane naraz, w galerii i w powiększeniu. -----

const playing = new Set();

function setPlaying(box, play) {
  const frame = box.querySelector(':scope > iframe');
  if (frame) {
    const youtube = frame.dataset.videoType === 'youtube';
    const command = youtube ? { event: 'command', func: play ? 'playVideo' : 'pauseVideo', args: '' } : { method: play ? 'play' : 'pause' };
    frame.contentWindow?.postMessage(JSON.stringify(command), '*');
  }
  const video = box.querySelector(':scope > video');
  if (video) play ? video.play().catch(() => {}) : video.pause();
  if (box.modelUI) play ? box.modelUI.play() : box.modelUI.pause();
  box.classList.toggle('is-paused', !play);
  const toggle = box.querySelector('[data-toggle]');
  toggle?.setAttribute('aria-label', play ? toggle.dataset.labelPause : toggle.dataset.labelPlay);
  if (play) playing.add(box);
  else playing.delete(box);
}

export function pauseMedia(root = document) {
  for (const box of playing) if (!box.isConnected || root.contains(box)) setPlaying(box, false);
}

export function playMedia(box) {
  for (const other of playing) if (other !== box) setPlaying(other, false);
  if (!box.classList.contains('is-loaded')) {
    const player = box.querySelector(':scope > template')?.content.firstElementChild?.cloneNode(true);
    if (!player) return;
    box.append(player);
    box.classList.add('is-loaded');
    box.querySelector('[data-toggle]')?.removeAttribute('hidden');
    if (player.localName === 'model-viewer') setupModel(box, player);
  }
  setPlaying(box, true);
}

export function toggleMedia(box) {
  if (box.classList.contains('is-paused')) playMedia(box);
  else setPlaying(box, false);
}

function setupModel(box, viewer) {
  loadStyle(MODEL_CSS);
  window.Shopify?.loadFeatures?.([{
    name: 'model-viewer-ui',
    version: '1.0',
    onLoad: async (errors) => {
      if (errors) return;
      // Safari rejestruje ModelViewerUI z opóźnieniem.
      for (let i = 0; i < 10 && !window.Shopify.ModelViewerUI; i++) await new Promise((resolve) => setTimeout(resolve, 50));
      if (!window.Shopify.ModelViewerUI || !viewer.isConnected) return;
      box.modelUI = new window.Shopify.ModelViewerUI(viewer);
      setPlaying(box, true);
      // Stuknięcie (bez przeciągania) zatrzymuje model; wznowienie daje nakładka Shopify.
      let start = null;
      viewer.addEventListener('pointerdown', (event) => (start = event));
      viewer.addEventListener('click', (event) => {
        if (start && Math.hypot(event.clientX - start.clientX, event.clientY - start.clientY) < 10) setPlaying(box, false);
      });
    },
  }]);
}

// ----- Karuzela -----

class MediaGallery extends HTMLElement {
  connectedCallback() {
    if (this.dataset.context !== 'pdp' || this.controller) return; // szybkie dodawanie: statyczna lista
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.thumbList = this.querySelector('[data-thumb-list]');
    this.counter = this.querySelector('[data-counter]');
    this.index = Math.max(0, this.slides.findIndex((slide) => !slide.inert));

    const controller = (this.controller = new AbortController());
    const on = (target, type, listener, options) => target?.addEventListener(type, listener, { signal: controller.signal, ...options });
    on(this, 'click', (event) => this.onClick(event));
    on(this, 'keydown', (event) => this.onKey(event));
    on(this, 'pointerover', (event) => this.onIntent(event), { passive: true });
    on(this, 'focusin', preloadZoom);
    on(document, EVENTS.variantChange, (event) => this.onVariant(event));
    if (this.slides.length > 1) {
      on(this.track, 'scroll', () => this.onScroll(), { passive: true });
      on(this.track, 'pointerdown', (event) => this.onPointerDown(event));
      on(this.track, 'dragstart', (event) => event.preventDefault());
      on(this.track, 'touchstart', () => this.finishScroll?.(), { passive: true });
    }
    if (this.thumbList) {
      on(this.thumbList, 'scroll', () => this.mask(), { passive: true });
      this.resizer = new ResizeObserver(() => this.mask());
      this.resizer.observe(this.thumbList);
    }
    afterLoad(() => {
      if (this.controller !== controller) return;
      this.warm(this.index);
      preloadZoom();
    });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.drag?.abort();
    this.resizer?.disconnect();
    cancelAnimationFrame(this.frame);
    cancelAnimationFrame(this.maskFrame);
    this.frame = this.maskFrame = 0;
    this.finishScroll?.();
    // Usunięcie galerii z otwartym powiększeniem (edytor, podmiana fragmentu) nie może zostawić blokady przewijania.
    this.zoomer?.cleanup();
  }

  // Zmiana wariantu bez podmiany galerii (ten sam zestaw mediów): pokaż zdjęcie wariantu.
  onVariant({ detail, target }) {
    if (String(detail?.productId) !== this.dataset.productId) return;
    const scope = this.closest('dialog, .shopify-section');
    if (target !== document && scope && !scope.contains(target)) return;
    this.showMedia(detail.variant?.featured_media_id, { animate: false });
  }

  showMedia(mediaId, options) {
    const index = this.slides?.findIndex((slide) => slide.dataset.mediaId === String(mediaId)) ?? -1;
    if (index >= 0) this.select(index, options);
  }

  // Karuzela zapętlona. Skok dalej niż o sąsiada wygląda jak jeden krok: cel staje na chwilę
  // obok bieżącego slajdu (CSS order), przewija się płynnie, potem wraca na miejsce bez animacji (FR-12-05).
  select(to, { animate = !reducedMotion(), dir } = {}) {
    const count = this.slides?.length || 0;
    if (count < 2) return;
    to = ((to % count) + count) % count;
    const from = this.index;
    if (to === from) return;
    this.finishScroll?.();
    this.setActive(to);
    const width = this.track.clientWidth;
    dir = dir || Math.sign(to - from);
    if (!animate || to - from === dir) return this.scrollTrack(to * width, animate);
    const forward = dir > 0;
    this.slides.forEach((slide, i) => (slide.style.order = i === to ? 1 : (forward ? i <= from : i < from) ? 0 : 2));
    const position = from - (to < from ? 1 : 0) + (forward ? 0 : 1);
    this.track.scrollTo({ left: position * width, behavior: 'instant' });
    this.scrollTrack((position + dir) * width, true, () => {
      for (const slide of this.slides) slide.style.order = '';
      this.track.scrollTo({ left: to * width, behavior: 'instant' });
    });
  }

  scrollTrack(left, smooth, done) {
    this.moving = true;
    this.track.scrollTo({ left, behavior: smooth ? 'smooth' : 'instant' });
    this.finishScroll = afterScroll(this.track, () => {
      this.moving = false;
      this.finishScroll = null;
      done?.();
    });
  }

  setActive(index) {
    const previous = this.index;
    this.index = index;
    // Niewidoczne slajdy poza drzewem dostępności i kolejnością Tab.
    this.slides.forEach((slide, i) => (slide.inert = i !== index));
    for (const list of [this.thumbs, this.dots]) {
      list[previous]?.setAttribute('aria-current', 'false');
      list[index]?.setAttribute('aria-current', 'true');
    }
    if (this.counter) this.counter.textContent = `${index + 1} / ${this.slides.length}`;
    const list = this.thumbList;
    const thumb = this.thumbs[index];
    if (list?.clientHeight && thumb) {
      const top = thumb.offsetTop - (list.clientHeight - thumb.offsetHeight) / 2;
      list.scrollTo({ top, behavior: reducedMotion() ? 'instant' : 'smooth' });
    }
    this.warm(index);
  }

  // Dalsze slajdy mają loading="lazy" — sąsiadów aktywnego wczytujemy z wyprzedzeniem.
  warm(index) {
    const count = this.slides.length;
    for (const i of [index - 1, index, index + 1]) {
      const image = this.slides[(i + count) % count]?.querySelector('img[loading="lazy"]');
      if (image) image.loading = 'eager';
    }
  }

  mask() {
    this.maskFrame ||= requestAnimationFrame(() => {
      this.maskFrame = 0;
      scrollMask(this.thumbList);
    });
  }

  onScroll() {
    if (this.moving || this.frame || this.classList.contains('is-dragging')) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      const index = Math.round(Math.abs(this.track.scrollLeft) / this.track.clientWidth);
      if (index !== this.index && this.slides[index]) this.setActive(index);
    });
  }

  onIntent(event) {
    preloadZoom();
    const thumb = event.target.closest?.('[data-thumb]');
    if (thumb) this.warm(Number(thumb.dataset.thumb));
  }

  onClick(event) {
    const button = event.target.closest('button');
    if (!button || this.dragged || button.closest('dialog')) return;
    const { step, thumb, dot } = button.dataset;
    if (step) return this.select(this.index + Number(step), { dir: Number(step) });
    if (thumb ?? dot) return this.select(Number(thumb ?? dot));
    const slide = button.closest('[data-slide]');
    if (button.hasAttribute('data-zoom')) return this.zoom(this.slides.indexOf(slide), slide);
    const box = button.closest('[data-deferred]');
    if (button.hasAttribute('data-play')) playMedia(box);
    else if (button.hasAttribute('data-toggle')) toggleMedia(box);
  }

  // Klawiatura (naprawa BG-17/OP-27): ←/→ (↑/↓ na pasku miniatur), Home, End; fokus idzie za wyborem.
  onKey(event) {
    // Strzałki w uruchomionym modelu 3D obracają model.
    if (this.slides.length < 2 || event.altKey || event.ctrlKey || event.metaKey || event.target.closest('dialog, model-viewer')) return;
    const inThumbs = event.target.closest('[data-thumb-list]');
    const steps = { ArrowLeft: -1, ArrowRight: 1, ...(inThumbs && { ArrowUp: -1, ArrowDown: 1 }) };
    let to;
    if (event.key === 'Home') to = 0;
    else if (event.key === 'End') to = this.slides.length - 1;
    else if (steps[event.key]) to = this.index + steps[event.key];
    else return;
    event.preventDefault();
    const origin = event.target.closest('[data-thumb], [data-dot], [data-slide]');
    this.select(to, { dir: steps[event.key] });
    let next = null;
    if (origin?.hasAttribute('data-thumb')) next = this.thumbs[this.index];
    else if (origin?.hasAttribute('data-dot')) next = this.dots[this.index];
    else if (origin) next = this.slides[this.index].querySelector('button');
    next?.focus({ preventScroll: true });
  }

  // Przeciąganie myszą z inercją (FR-12-05): rzut o jeden slajd przy prędkości ≥ 10 px/s i drodze ≥ 10 px.
  onPointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('model-viewer')) return;
    event.preventDefault();
    this.finishScroll?.();
    const { track } = this;
    const startTime = performance.now();
    let last = event.clientX;
    let velocity = 0;
    let travelled = 0;
    let moved = false;
    this.drag?.abort();
    const drag = (this.drag = new AbortController());
    const move = (moveEvent) => {
      const delta = last - moveEvent.clientX;
      if (!delta) return;
      if (!moved) this.classList.add('is-dragging');
      moved = true;
      velocity = Math.round((delta / (performance.now() - startTime)) * 1000);
      last = moveEvent.clientX;
      travelled += Math.abs(delta);
      track.scrollLeft += delta;
    };
    const end = () => {
      drag.abort();
      if (!moved) return;
      // Klik kończący przeciąganie nie otwiera powiększenia.
      this.dragged = true;
      setTimeout(() => (this.dragged = false));
      const max = this.slides.length - 1;
      const nearest = clamp(Math.round(Math.abs(track.scrollLeft) / track.clientWidth), 0, max);
      const step = nearest !== this.index || Math.abs(velocity) < 10 || travelled < 10 ? 0 : Math.sign(velocity);
      const to = clamp(nearest + step, 0, max);
      if (to !== this.index) this.setActive(to);
      this.scrollTrack(to * track.clientWidth, !reducedMotion(), () => this.classList.remove('is-dragging'));
    };
    document.addEventListener('pointermove', move, { signal: drag.signal });
    document.addEventListener('pointerup', end, { signal: drag.signal });
    document.addEventListener('pointercancel', end, { signal: drag.signal });
  }

  async zoom(index, source) {
    if (this.zooming || index < 0) return;
    this.zooming = true;
    try {
      const { Zoom } = await import('@theme/zoom');
      if (!this.isConnected) return;
      this.zoomer ??= new Zoom(this);
      await this.zoomer.open(index, source);
    } catch (error) {
      console.error(error);
    } finally {
      this.zooming = false;
    }
  }
}

if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);
